import { useState, useEffect, useMemo } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar as CalIcon, MapPin, Clock, Loader2 } from 'lucide-react';
import { api, mediaUrl } from '@/admin/api';

const EventsPage = () => {
  const [tab, setTab] = useState('upcoming');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/events').then((r) => setEvents(r.data)).finally(() => setLoading(false));
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const { upcoming, past } = useMemo(() => {
    const upcoming = events.filter((e) => e.date >= today).sort((a, b) => a.date.localeCompare(b.date));
    const past = events.filter((e) => e.date < today).sort((a, b) => b.date.localeCompare(a.date));
    return { upcoming, past };
  }, [events, today]);

  const visible = tab === 'upcoming' ? upcoming : past;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="relative bg-gradient-to-br from-primary/10 via-background to-accent/10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
            <CalIcon size={18} />
            <span className="text-sm font-medium">Events Calendar</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
            Events & Activities
          </h1>
          <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">
            Stay informed about our upcoming events, workshops, and cultural celebrations.
          </p>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12">
              <TabsTrigger value="upcoming" className="text-base" data-testid="events-tab-upcoming">
                Upcoming ({upcoming.length})
              </TabsTrigger>
              <TabsTrigger value="past" className="text-base" data-testid="events-tab-past">
                Past ({past.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value={tab} className="space-y-8">
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
              ) : visible.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground">No {tab} events.</div>
              ) : (
                visible.map((event) => (
                  <Card key={event.id} className="overflow-hidden hover:shadow-elevated transition-elegant" data-testid={`event-${event.id}`}>
                    <div className="grid md:grid-cols-3 gap-0">
                      {event.image_url && (
                        <div className="md:col-span-1 h-64 md:h-auto">
                          <img src={mediaUrl(event.image_url)} alt={event.title} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className={event.image_url ? 'md:col-span-2' : 'md:col-span-3'}>
                        <CardContent className="p-8 h-full flex flex-col">
                          <div className="flex items-start justify-between mb-4">
                            <Badge className="bg-primary/10 text-primary hover:bg-primary/20">{event.category || 'event'}</Badge>
                          </div>
                          <h3 className="text-2xl font-serif font-bold text-foreground mb-4">{event.title}</h3>
                          <p className="text-muted-foreground leading-relaxed mb-6 flex-grow whitespace-pre-wrap">{event.description}</p>
                          <div className="space-y-3">
                            <div className="flex items-center text-sm text-foreground"><CalIcon size={18} className="mr-3 text-primary" /><span>{event.date}</span></div>
                            {event.time && <div className="flex items-center text-sm text-foreground"><Clock size={18} className="mr-3 text-primary" /><span>{event.time}</span></div>}
                            {event.location && <div className="flex items-center text-sm text-foreground"><MapPin size={18} className="mr-3 text-primary" /><span>{event.location}</span></div>}
                          </div>
                        </CardContent>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EventsPage;
